import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'

export default function SignAfterContractCertificatePage() {
  const { token } = useParams()
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [done, setDone] = useState(false)
  const canvasRef = useRef(null)
  const ctxRef = useRef(null)
  const drawingRef = useRef(false)
  const lastRef = useRef({ x: 0, y: 0 })
  const hasInkRef = useRef(false)
  const [hasInk, setHasInk] = useState(false)
  const handwritingCanvasRef = useRef(null)
  const handwritingCtxRef = useRef(null)
  const handwritingDrawingRef = useRef(false)
  const handwritingLastRef = useRef({ x: 0, y: 0 })
  const handwritingHasInkRef = useRef(false)
  const [hasHandwritingInk, setHasHandwritingInk] = useState(false)
  const insuredHandwritingCanvasRef = useRef(null)
  const insuredHandwritingCtxRef = useRef(null)
  const insuredHandwritingDrawingRef = useRef(false)
  const insuredHandwritingLastRef = useRef({ x: 0, y: 0 })
  const insuredHandwritingHasInkRef = useRef(false)
  const [hasInsuredHandwritingInk, setHasInsuredHandwritingInk] = useState(false)

  const apiVersion = process.env.REACT_APP_API_VERSION || 'v1'
  const baseUrl = useMemo(() => `/${apiVersion}`.replace(/\/{2,}/g, '/'), [apiVersion])

  const setupCanvasElement = useCallback((canvas, ctxTargetRef) => {
    if (!canvas) return
    const parent = canvas.parentElement
    const cssW = parent ? parent.clientWidth : 320
    const cssH = Math.max(180, Math.min(320, Math.round(cssW * 0.55)))
    const dpr = window.devicePixelRatio || 1
    canvas.style.width = `${cssW}px`
    canvas.style.height = `${cssH}px`
    canvas.width = Math.floor(cssW * dpr)
    canvas.height = Math.floor(cssH * dpr)
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctxTargetRef.current = ctx
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = 3
    ctx.strokeStyle = '#0f172a'
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, cssW, cssH)
  }, [])

  const setupCanvas = useCallback(() => {
    setupCanvasElement(canvasRef.current, ctxRef)
    setupCanvasElement(handwritingCanvasRef.current, handwritingCtxRef)
    setupCanvasElement(insuredHandwritingCanvasRef.current, insuredHandwritingCtxRef)
  }, [setupCanvasElement])

  const clear = useCallback(() => {
    const reset = (canvas, ctx) => {
      if (!canvas || !ctx) return
      ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    }
    reset(canvasRef.current, ctxRef.current)
    reset(handwritingCanvasRef.current, handwritingCtxRef.current)
    reset(insuredHandwritingCanvasRef.current, insuredHandwritingCtxRef.current)
    hasInkRef.current = false
    handwritingHasInkRef.current = false
    insuredHandwritingHasInkRef.current = false
    setHasInk(false)
    setHasHandwritingInk(false)
    setHasInsuredHandwritingInk(false)
  }, [])

  const clearHandwriting = useCallback(() => {
    const canvas = handwritingCanvasRef.current
    const ctx = handwritingCtxRef.current
    if (!canvas || !ctx) return
    ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    handwritingHasInkRef.current = false
    setHasHandwritingInk(false)
  }, [])

  const clearInsuredHandwriting = useCallback(() => {
    const canvas = insuredHandwritingCanvasRef.current
    const ctx = insuredHandwritingCtxRef.current
    if (!canvas || !ctx) return
    ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    insuredHandwritingHasInkRef.current = false
    setHasInsuredHandwritingInk(false)
  }, [])

  useEffect(() => {
    setupCanvas()
    const onResize = () => {
      setupCanvas()
      clear()
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [setupCanvas, clear])

  useEffect(() => {
    if (loading || error || done) return
    const t = setTimeout(setupCanvas, 50)
    return () => clearTimeout(t)
  }, [loading, error, done, data, setupCanvas])

  useEffect(() => {
    let ignore = false
    ;(async () => {
      if (!token) return
      setLoading(true)
      setError(null)
      try {
        const res = await axios.get(`${baseUrl}/after-contract/certificates/public/${token}`)
        const payload = res?.data?.data !== undefined ? res.data.data : res.data
        if (!ignore) {
          setData(payload)
          setDone(String(payload?.status || '').toUpperCase() === 'COMPLETED')
        }
      } catch {
        if (!ignore) setError('링크가 만료되었거나 유효하지 않습니다.')
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => { ignore = true }
  }, [token, baseUrl])

  const getPoint = (e, ref = canvasRef) => {
    const rect = ref.current?.getBoundingClientRect()
    return rect ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : { x: 0, y: 0 }
  }

  const onPointerDown = (e) => {
    const canvas = canvasRef.current
    const ctx = ctxRef.current
    if (!canvas || !ctx) return
    e.preventDefault()
    drawingRef.current = true
    const p = getPoint(e)
    lastRef.current = p
    try { canvas.setPointerCapture(e.pointerId) } catch {}
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + 0.01, p.y + 0.01)
    ctx.stroke()
    hasInkRef.current = true
    setHasInk(true)
  }

  const onPointerMove = (e) => {
    const ctx = ctxRef.current
    if (!drawingRef.current || !ctx) return
    e.preventDefault()
    const p = getPoint(e)
    const last = lastRef.current
    ctx.quadraticCurveTo(last.x, last.y, (last.x + p.x) / 2, (last.y + p.y) / 2)
    ctx.stroke()
    lastRef.current = p
  }

  const endDrawing = (e) => {
    if (!drawingRef.current) return
    drawingRef.current = false
    try { ctxRef.current?.closePath() } catch {}
    try { canvasRef.current?.releasePointerCapture(e.pointerId) } catch {}
  }

  const onHandwritingPointerDown = (e) => {
    const canvas = handwritingCanvasRef.current
    const ctx = handwritingCtxRef.current
    if (!canvas || !ctx) return
    e.preventDefault()
    handwritingDrawingRef.current = true
    const p = getPoint(e, handwritingCanvasRef)
    handwritingLastRef.current = p
    try { canvas.setPointerCapture(e.pointerId) } catch {}
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + 0.01, p.y + 0.01)
    ctx.stroke()
    handwritingHasInkRef.current = true
    setHasHandwritingInk(true)
  }

  const onHandwritingPointerMove = (e) => {
    const ctx = handwritingCtxRef.current
    if (!handwritingDrawingRef.current || !ctx) return
    e.preventDefault()
    const p = getPoint(e, handwritingCanvasRef)
    const last = handwritingLastRef.current
    ctx.quadraticCurveTo(last.x, last.y, (last.x + p.x) / 2, (last.y + p.y) / 2)
    ctx.stroke()
    handwritingLastRef.current = p
  }

  const endHandwritingDrawing = (e) => {
    if (!handwritingDrawingRef.current) return
    handwritingDrawingRef.current = false
    try { handwritingCtxRef.current?.closePath() } catch {}
    try { handwritingCanvasRef.current?.releasePointerCapture(e.pointerId) } catch {}
  }

  const onInsuredHandwritingPointerDown = (e) => {
    const canvas = insuredHandwritingCanvasRef.current
    const ctx = insuredHandwritingCtxRef.current
    if (!canvas || !ctx) return
    e.preventDefault()
    insuredHandwritingDrawingRef.current = true
    const p = getPoint(e, insuredHandwritingCanvasRef)
    insuredHandwritingLastRef.current = p
    try { canvas.setPointerCapture(e.pointerId) } catch {}
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + 0.01, p.y + 0.01)
    ctx.stroke()
    insuredHandwritingHasInkRef.current = true
    setHasInsuredHandwritingInk(true)
  }

  const onInsuredHandwritingPointerMove = (e) => {
    const ctx = insuredHandwritingCtxRef.current
    if (!insuredHandwritingDrawingRef.current || !ctx) return
    e.preventDefault()
    const p = getPoint(e, insuredHandwritingCanvasRef)
    const last = insuredHandwritingLastRef.current
    ctx.quadraticCurveTo(last.x, last.y, (last.x + p.x) / 2, (last.y + p.y) / 2)
    ctx.stroke()
    insuredHandwritingLastRef.current = p
  }

  const endInsuredHandwritingDrawing = (e) => {
    if (!insuredHandwritingDrawingRef.current) return
    insuredHandwritingDrawingRef.current = false
    try { insuredHandwritingCtxRef.current?.closePath() } catch {}
    try { insuredHandwritingCanvasRef.current?.releasePointerCapture(e.pointerId) } catch {}
  }

  const submit = async () => {
    const needsInsuredHandwriting = role === 'CONTRACT_HOLDER' && Boolean(data?.insuredSameAsContractor)
    if (!hasInkRef.current) {
      setError('서명을 입력해 주세요.')
      return
    }
    if (!handwritingHasInkRef.current) {
      setError(`자필 문구 "${handwritingLabel}"를 입력해 주세요.`)
      return
    }
    if (needsInsuredHandwriting && !insuredHandwritingHasInkRef.current) {
      setError('피보험자 자필 문구 "성실히 고지"를 입력해 주세요.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png')
      const handwritingDataUrl = handwritingCanvasRef.current.toDataURL('image/png')
      const additionalInsuredHandwritingDataUrl = needsInsuredHandwriting
        ? insuredHandwritingCanvasRef.current.toDataURL('image/png')
        : null
      const res = await axios.post(`${baseUrl}/after-contract/certificates/public/${token}/complete`, { dataUrl, handwritingDataUrl, additionalInsuredHandwritingDataUrl })
      const payload = res?.data?.data !== undefined ? res.data.data : res.data
      setData(payload)
      setDone(true)
    } catch {
      setError('서명 제출에 실패했습니다. 링크 만료 여부를 확인해 주세요.')
    } finally {
      setSubmitting(false)
    }
  }

  const role = String(data?.signerRole || '').toUpperCase()
  const roleLabel = role === 'CONTRACT_HOLDER' ? '계약자' : '피보험자'
  const handwritingLabel = role === 'CONTRACT_HOLDER' ? '설명을 듣고 이해' : '성실히 고지'

  return (
    <section className="mx-auto max-w-xl px-4 py-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">3종확인서 {roleLabel} 서명</h1>
        <Link to="/login" className="text-sm text-slate-600 hover:underline">로그인</Link>
      </div>
      {loading && <div className="mt-6 text-sm text-slate-600">불러오는 중...</div>}
      {error && <div className="mt-6 rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>}
      {!loading && !error && (
        <div className="mt-6 space-y-4">
          <div className="rounded-2xl border bg-white p-5">
            <div className="text-sm text-slate-600">안내</div>
            <div className="mt-1 text-slate-900">
              {data?.signerName ? `${data.signerName} 님(${roleLabel}), 아래 영역에 서명과 자필 문구를 입력해 주세요.` : `${roleLabel} 본인 확인 후 서명과 자필 문구를 입력해 주세요.`}
            </div>
            <div className="mt-2 text-xs text-slate-500">모바일은 손가락, PC는 마우스로 직접 입력할 수 있습니다.</div>
          </div>
          {done ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
              서명과 자필 입력이 완료되었습니다. 창을 닫아도 됩니다.
            </div>
          ) : (
            <div className="rounded-2xl border bg-white p-4 space-y-4">
              <div>
                <div className="mb-1 text-xs font-semibold text-slate-600">{roleLabel} 서명</div>
                <canvas
                  ref={canvasRef}
                  className="block w-full touch-none rounded-xl border bg-white"
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={endDrawing}
                  onPointerCancel={endDrawing}
                  onPointerLeave={endDrawing}
                />
              </div>
              <div>
                <div className="mb-1 text-xs font-semibold text-slate-600">{roleLabel} 자필: “{handwritingLabel}”</div>
                <canvas
                  ref={handwritingCanvasRef}
                  className="block w-full touch-none rounded-xl border bg-white"
                  onPointerDown={onHandwritingPointerDown}
                  onPointerMove={onHandwritingPointerMove}
                  onPointerUp={endHandwritingDrawing}
                  onPointerCancel={endHandwritingDrawing}
                  onPointerLeave={endHandwritingDrawing}
                />
              </div>
              {role === 'CONTRACT_HOLDER' && Boolean(data?.insuredSameAsContractor) && (
                <div>
                  <div className="mb-1 text-xs font-semibold text-slate-600">피보험자 자필: “성실히 고지”</div>
                  <canvas
                    ref={insuredHandwritingCanvasRef}
                    className="block w-full touch-none rounded-xl border bg-white"
                    onPointerDown={onInsuredHandwritingPointerDown}
                    onPointerMove={onInsuredHandwritingPointerMove}
                    onPointerUp={endInsuredHandwritingDrawing}
                    onPointerCancel={endInsuredHandwritingDrawing}
                    onPointerLeave={endInsuredHandwritingDrawing}
                  />
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={clear} className="rounded border px-3 py-2 text-sm">전체 지우기</button>
                <button type="button" onClick={clearHandwriting} className="rounded border px-3 py-2 text-sm">자필만 지우기</button>
                {role === 'CONTRACT_HOLDER' && Boolean(data?.insuredSameAsContractor) && (
                  <button type="button" onClick={clearInsuredHandwriting} className="rounded border px-3 py-2 text-sm">피보험자 자필 지우기</button>
                )}
                <button
                  type="button"
                  disabled={!hasInk || !hasHandwritingInk || (role === 'CONTRACT_HOLDER' && Boolean(data?.insuredSameAsContractor) && !hasInsuredHandwritingInk) || submitting}
                  onClick={submit}
                  className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {submitting ? '제출 중...' : '서명/자필 제출'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
